import styled, { css } from 'styled-components';
import React, { useMemo } from 'react';
import moment from 'moment';
import Countdown from './Countdown';
import { useSelector } from 'react-redux';
import { selectInfoUser, selectTranslations } from '../App/slice/selectors';
import {
  HolidayOrderStateValue,
  HolidayPaymentStatus,
  IHolidayOrderDetail,
} from '../HolidayCheckout/types';
import { usePassengerCount } from '../PaymentHolidayInformation/hooks/usePassengerCount';
import _ from 'lodash';
import { Permission } from 'app/hooks/usePermission';
interface Props {
  orderState: HolidayOrderStateValue;
  dataInfo: IHolidayOrderDetail;
  onClickPaymentNow?: () => void;
  onTimeout?: () => void;
  onCancel?: () => void;
  onReject?: () => void;
  onClickApprove?: () => void;
}
const OrderCard = ({
  orderState,
  dataInfo,
  onClickPaymentNow,
  onTimeout,
  onCancel,
  onReject,
  onClickApprove,
}: Props) => {
  const translations = useSelector(selectTranslations);
  const { textPassenger } = usePassengerCount(dataInfo.passengers);
  const infoUser = useSelector(selectInfoUser);
  const isSaleMng =
    infoUser?.role_code === Permission.ROLE_SALE_MANAGER ||
    infoUser?.role_code === Permission.ROLE_SALE_SHG;

  const isAllowPay = useMemo(() => {
    const subOrderState = _.isEmpty(dataInfo?.subOrderState) ? true : false;
    return subOrderState;
  }, [dataInfo?.subOrderState]);
  return (
    <Card>
      <Content>
        <InfoGrid>
          <InfoItem>
            {translations?.['b2c.txt.orderNumber']}
            <strong>{dataInfo?.id || ''}</strong>
          </InfoItem>
          <InfoItem>
            {translations?.['b2b.orderManagement.date-order-sap']}
            <strong>{dataInfo?.thirdPartySubmitNumber || ''}</strong>
          </InfoItem>
          <InfoItem>
            {translations?.['b2b.txt.passenger']}
            <strong>{textPassenger}</strong>
          </InfoItem>
          <InfoItem>
            {translations?.['b2b.txt.bookingDate']}
            <strong>
              {moment(dataInfo?.createdAt, 'HH:mm:ss DD/MM/YYYY').format(
                'DD/MM/YYYY',
              )}
            </strong>
          </InfoItem>
          <InfoItem>
            {translations?.['b2b.txt.orderer']}
            <strong>{dataInfo?.agent?.name}</strong>
          </InfoItem>
        </InfoGrid>
        {(orderState === 'success' ||
          orderState === 'syncHoliday' ||
          orderState === 'syncSap') && (
          <StatusBadge status="success">{`● ${
            dataInfo?.issueTicket || ''
          } `}</StatusBadge>
        )}
        {orderState === 'paymentPending' && (
          <PendingRight>
            <PendingStatusBox>
              <div className="title">{`● ${translations?.['b2b.option.pending']}`}</div>
              <div className="time">
                {translations?.['b2b.txt.payInRemain']}:{' '}
                <span>
                  {' '}
                  <Countdown onTimeout={onTimeout} orderCheckout={dataInfo} />
                </span>
              </div>
            </PendingStatusBox>
          </PendingRight>
        )}
        {orderState === 'cancelled' && (
          <StatusBadge status="cancelled">
            {translations?.['b2b.txt.ticket-cancelled']}
          </StatusBadge>
        )}
      </Content>
      {((orderState === 'paymentPending' &&
        dataInfo?.subOrderState?.value === 'pendingConfirmation' &&
        !isSaleMng) ||
        (orderState === 'paymentPending' && isAllowPay)) && (
        <Footer>
          <Actions>
            <CancelBtn onClick={onCancel}>
              {translations?.['b2b.txt.cancelBooking']}
            </CancelBtn>
            <PayBtn
              onClick={onClickPaymentNow}
              disabled={!isAllowPay || dataInfo?.agent?.id !== infoUser?.id}
              isDisable={!isAllowPay || dataInfo?.agent?.id !== infoUser?.id}
            >
              {translations?.['b2b.txt.pay-now']}
            </PayBtn>
          </Actions>
        </Footer>
      )}
      {orderState === 'paymentPending' &&
        dataInfo?.subOrderState?.value === 'pendingConfirmation' &&
        isSaleMng && (
          <Footer>
            <Actions>
              <CancelBtn onClick={onReject}>
                {translations?.['b2b.approvalStt.refuse']}
              </CancelBtn>
              <PayBtn
                onClick={onClickApprove}
                disabled={!isSaleMng}
                isDisable={!isSaleMng}
              >
                {translations?.['b2b.btn.accept']}
              </PayBtn>
            </Actions>
          </Footer>
        )}
      {orderState === 'paymentPending' &&
        dataInfo?.subOrderState?.value === 'pendingConfirmation' &&
        !isSaleMng && (
          <NoteWaiting>
            {translations?.['b2b.txt.orderWaitingForApproval']}
          </NoteWaiting>
        )}
    </Card>
  );
};
export default OrderCard;
const Actions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  @media (max-width: 576px) {
    width: 100%;
    flex-direction: column;

    button {
      width: 100%;
    }
  }
`;
const CancelBtn = styled.button`
  background: transparent;
  color: var(--color-9);
  border: 1px solid var(--color-9);
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    opacity: 0.8;
  }
`;
const PayBtn = styled.button<{ isDisable?: boolean }>`
  background: var(--color-gradient);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  &:hover {
    opacity: 0.8;
  }
  :disabled {
    background: var(--color-8);
  }
  cursor: ${props => (props.isDisable ? 'not-allowed' : 'pointer')};
`;
const Card = styled.div`
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
`;
const Content = styled.div`
  background: #fff;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  @media (max-width: 992px) {
    flex-direction: column;
    align-items: stretch;
  }
`;
const Footer = styled.div`
  background: #f3f3f3;
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  @media (max-width: 576px) {
    justify-content: center;
  }
`;
const NoteWaiting = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-style: italic;
  color: var(--color-9);
`;
const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, auto);
  gap: 24px;
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const InfoItem = styled.div`
  font-size: 12px;
  color: #6b7280;
  text-transform: lowercase;
  &::first-letter {
    text-transform: uppercase;
  }
  strong {
    text-transform: none;
    display: block;
    font-size: 14px;
    color: #1a202c;
    margin-top: 4px;
  }
`;
export const StatusBadge = styled.div<{ status: HolidayPaymentStatus }>`
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  ${({ status }) =>
    status === 'success' &&
    css`
      background: #e7f8ef;
      color: #16a34a;
    `}

  ${({ status }) =>
    status === 'pending' &&
    css`
      background: #fff1e3;
      color: var(--color-9);
    `}

  ${({ status }) =>
    status === 'cancelled' &&
    css`
      background: #f2f2f2;
    `}
`;
const PendingRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  @media (max-width: 992px) {
    align-items: flex-start;
    width: 100%;
  }
`;
const PendingStatusBox = styled.div`
  background: #fff1e3;
  color: var(--color-9);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  min-width: 240px;
  @media (max-width: 992px) {
    width: 100%;
    min-width: unset;
  }
  .title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    margin-bottom: 4px;
    justify-content: flex-end;
    @media (max-width: 992px) {
      justify-content: flex-start;
    }
  }

  .time {
    color: #111827;
    font-weight: 600;
    span {
      color: #ef4444;
    }
  }
`;
